import { useReducer, useState } from "react";
import { useDispatch } from "react-redux";
import { QRCodeSVG } from "qrcode.react";
import { setLocalStorageItem } from "../../../reducers/loginLocalStorage";
import { useAppSelector } from "../../../store";
import { DeviceType, ImpersonationDetails, ImpersonationType } from "../../../common/interfaces/impersonation";
import { CopyToClipboard } from "../../../common/components/CopyToClipboard/CopyToClipboard";
import { IconWorld } from "../../../common/icons/IconWorld/IconWorld";
import { IconMobilePhone } from "../../../common/icons/IconMobilePhone/IconMobilePhone";
import { IconAcademy } from "../../../common/icons/IconAcademy/IconAcademy";
import { LeafSearchDropdown } from "../../../common/leaf/LeafSearchDropdown/LeafSearchDropdown";
import { AFF_MARKET_MAP } from "../../../common/constants/aff-market-map";
import { LeafToggleSwitch } from "../../../common/leaf/LeafToggleSwitch/LeafToggleSwitch";
import { LeafModal } from "../../../common/leaf/LeafModal/LeafModal";
import {
  authorization,
  affiliates,
  createAmwayPlusImpersonationURL,
  createAcademyImpersonationURL,
} from "../impersonation-util";
import "./Impersonation.scss";
import { localeCodes } from "../../../common/constants/supportedLocales";
import { Button } from "../../../common/components/Button/Button";
import { trackPreLoginImpersonationDetails } from "../../../common/util/tealium";
import { ImpersonationHistory, updateImpersonationHistory } from "./ImpersonationHistory/ImpersonationHistory";

const formReducer = (state: any, event: any) => {
  return {
    ...state,
    [event.name]: event.value,
  };
};

export const getDeviceType = (): DeviceType => {
  const userAgent = navigator.userAgent;
  const isMobileDevice = /android|iphone|ipad|iPod/i.test(userAgent.toLowerCase());

  if (isMobileDevice) {
    return "mobile";
  }
  return "desktop";
};

const getImpersonationType = (nativeEvent: SubmitEvent): ImpersonationType => {
  const submitter = (nativeEvent as SubmitEvent).submitter as HTMLButtonElement;
  if (submitter?.id === "amwayPlusImpersonation") {
    return "amwayPlus";
  }
  if (submitter?.id === "myBizWebImpersonation") {
    return "myBizWeb";
  }
  if (submitter?.id === "academyWebImpersonation") {
    return "academyWeb";
  }

  return "myBizWeb";
};

export const Impersonation = () => {
  const { impersonationHistory } = useAppSelector((state) => state.loginLocalStorage);
  const dispatch = useDispatch();

  const [formData, setFormData] = useReducer(formReducer, {
    abo: 0,
    country: 0,
    partyId: "12345",
    "is-jwt": true,
    locale: "en-US",
    note: "",
  });

  const createLocaleMap = () => {
    const localeMap: { [key: string]: string } = {};

    if (window.location.origin.includes("localhost")) {
      localeMap["ln-HT"] = `(ln-HT) - Xxxx Xxxx (long)`;
    }

    localeCodes
      .slice()
      .sort((a, b) => (a.code > b.code ? 1 : -1))
      .map((locale) => (localeMap[locale.code] = `(${locale.code}) - ${locale.locale}`));
    return localeMap;
  };

  const [historyImpn, setHistoryImpn] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showPartyId, setShowPartyId] = useState(true);
  const [showQRCodeModal, setShowQRCodeModal] = useState(false);
  const [qrCodeURL, setQRCodeURL] = useState("");

  const qrCodeModalToggle = () => {
    setShowQRCodeModal(!showQRCodeModal);
  };

  const handleAmwayPlus = async (impersonationDetails: ImpersonationDetails) => {
    const { deviceType, sameWindow } = impersonationDetails;
    const history = updateImpersonationHistory(impersonationHistory, impersonationDetails);
    dispatch(setLocalStorageItem({ impersonationHistory: history }));

    const amwayPlusUrl = await createAmwayPlusImpersonationURL(impersonationDetails);
    if (!amwayPlusUrl) {
      console.error("Failed to generate Amway Plus URL");
      return;
    }

    if (deviceType === "desktop") {
      setQRCodeURL(amwayPlusUrl);
      qrCodeModalToggle();
      return;
    }
    if (deviceType === "mobile") {
      window.open(amwayPlusUrl, sameWindow ? "_self" : "_blank");
    }
  };

  const handleAcademy = async (impersonationDetails: ImpersonationDetails) => {
    const { sameWindow } = impersonationDetails;
    const history = updateImpersonationHistory(impersonationHistory, impersonationDetails);
    dispatch(setLocalStorageItem({ impersonationHistory: history }));

    const academyUrl = await createAcademyImpersonationURL(impersonationDetails);
    if (!academyUrl) {
      console.error("Failed to generate Academy URL");
      return;
    }

    window.open(academyUrl, sameWindow ? "_self" : "_blank");
  };

  const handleSubmit = async (event: React.FormEvent<EventTarget>) => {
    event.preventDefault();
    const impersonationType = getImpersonationType(event.nativeEvent as SubmitEvent);
    const currentDeviceType = getDeviceType();

    const impersonationDetails: ImpersonationDetails = {
      ...formData,
      impersonationType: impersonationType,
      deviceType: currentDeviceType,
    };

    trackPreLoginImpersonationDetails(impersonationDetails);

    if (impersonationType === "amwayPlus") {
      handleAmwayPlus(impersonationDetails);
    } else if (impersonationType === "academyWeb") {
      handleAcademy(impersonationDetails);
    } else {
      authorization(impersonationDetails);
    }

    const historyItem = {
      ...formData,
      spcode: "",
      impersonationType: impersonationType,
      deviceType: currentDeviceType,
    };

    if (impersonationType !== "entra") {
      setHistoryImpn(historyItem);
    }

    setTimeout(() => {
      setSubmitting(false);
      setFormData({
        reset: true,
      });
    }, 1000);
  };

  const impersonateHistory = (impersonationDetails: ImpersonationDetails) => {
    authorization({ ...impersonationDetails, locale: formData.locale });
  };

  const amwayPlusImpersonationCallback = async (impersonationDetails: ImpersonationDetails) => {
    handleAmwayPlus(impersonationDetails);
  };

  const academyImpersonationCallback = async (impersonationDetails: ImpersonationDetails) => {
    handleAcademy(impersonationDetails);
  };

  const handleChange = (event: any) => {
    const isJwtCheckboxEvent = event.target.type === "checkbox";
    if (isJwtCheckboxEvent) {
      const isJwtChecked = event.target.checked;
      setShowPartyId(isJwtChecked);
      setFormData({
        name: "partyId",
        value: isJwtChecked ? "12345" : "",
      });
    }
    const eventValue = event.target.name === "partyId" ? event.target.value.toString().slice(0, 9) : event.target.value;
    setFormData({
      name: event.target.name,
      value: isJwtCheckboxEvent ? event.target.checked : eventValue,
    });
  };

  const checkDefaultFields = () => {
    if (formData.abo > 0 && formData.country > 0 && formData.locale) {
      return true;
    }
    return false;
  };

  const renderCountryList = () => {
    return affiliates
      .sort((a, b) => (a.label > b.label ? 1 : -1))
      .map((aff, i) => {
        return (
          <div key={i}>
            {" "}
            {aff.label} - {aff.iso} - {aff.country}
          </div>
        );
      });
  };

  const isInValid = (): boolean => {
    if (formData["is-jwt"]) {
      if (checkDefaultFields() && formData.partyId) {
        return false;
      } else {
        return true;
      }
    } else {
      return !checkDefaultFields();
    }
  };

  const setCountry = (aff: string) => {
    setFormData({ name: "country", value: aff });
  };

  const setLocale = (locale: string) => {
    setFormData({ name: "locale", value: locale });
  };

  return (
    <div className="impersonation__container">
      <div className="impersonation">
        <h1 className="impersonation__heading">ABO Tools Impersonation</h1>
        <div className="impersonation__form">
          <form onSubmit={handleSubmit}>
            <fieldset disabled={submitting}>
              <div className="impersonation__formElements">
                <div className="impersonation__inputGroup">
                  <label htmlFor="impersonation__abo">ABO #</label>
                  <input
                    className="impersonation__input"
                    id="impersonation__abo"
                    data-testid="impersonation-abo-input"
                    type="number"
                    name="abo"
                    aria-label="ABO Number"
                    min={0}
                    onChange={handleChange}
                    value={formData.abo || ""}
                  />
                </div>
                <div className="impersonation__inputGroup">
                  <label htmlFor="impersonation__country-code">Country Code</label>
                  <div className="impersonation__searchDropdown">
                    <LeafSearchDropdown
                      handleSelect={setCountry}
                      optionsMap={AFF_MARKET_MAP}
                      placeholder="Search aff or country code"
                      id="impersonation__country-code"
                      data-testid="impersonation-country-code"
                    />
                  </div>
                </div>
                <div className={`impersonation__inputGroup ${!showPartyId ? "impersonation__disabled" : ""}`}>
                  <label htmlFor="impersonation__party-id" className={!showPartyId ? "impersonation__disabled" : ""}>
                    Party ID
                  </label>
                  <input
                    className="impersonation__input"
                    id="impersonation__party-id"
                    name="partyId"
                    aria-label="Party ID"
                    type="number"
                    min={0}
                    onChange={handleChange}
                    value={formData.partyId || ""}
                    disabled={!showPartyId}
                  />
                </div>
                <div className="impersonation__inputGroup">
                  <label htmlFor="impersonation__locale">Locale</label>
                  <div className="impersonation__searchDropdown">
                    <LeafSearchDropdown
                      handleSelect={setLocale}
                      optionsMap={createLocaleMap()}
                      placeholder="Search Locale"
                      selectedValue={formData.locale || ""}
                      id="impersonation__locale"
                    />
                  </div>
                </div>
                <div className="impersonation__inputGroup impersonation__inputNote">
                  <label htmlFor="impersonation__note">Note (optional)</label>
                  <input
                    className="impersonation__input"
                    id="impersonation__note"
                    name="note"
                    aria-label="Note"
                    onChange={handleChange}
                    value={formData.note || ""}
                  />
                </div>
                <div className="impersonation__inputSubmitGroup">
                  <Button
                    id="myBizWebImpersonation"
                    data-testid="impersonation-submit"
                    className="impersonation__submitButton"
                    type="submit"
                    disabled={isInValid()}
                  >
                    <IconWorld fill="white" />
                    <span>MyBiz Web</span>
                  </Button>
                  <Button
                    id="amwayPlusImpersonation"
                    className="impersonation__submitButton"
                    type="submit"
                    disabled={isInValid()}
                  >
                    <IconMobilePhone fill="white" />
                    <span>Amway+</span>
                  </Button>
                  <Button
                    id="academyWebImpersonation"
                    className="impersonation__submitButton"
                    type="submit"
                    disabled={isInValid()}
                  >
                    <IconAcademy color="white" />
                    <span>Academy</span>
                  </Button>
                </div>
              </div>
            </fieldset>
          </form>
        </div>
        <h2 className="impersonation__section-header impersonation__section-header--recently-used">Recently Used</h2>
        <div className="impersonation__recently-used">
          <ImpersonationHistory
            impersonationAdded={historyImpn}
            impersonationAuthCallback={impersonateHistory}
            amwayPlusImpersonationCallback={amwayPlusImpersonationCallback}
            academyImpersonationCallback={academyImpersonationCallback}
          ></ImpersonationHistory>
        </div>

        <h2 className="impersonation__section-header">Country Codes</h2>
        <div className="impersonation__country-list">
          <div id="impersonation__country-list-list" className="impersonation__country-list-list">
            {renderCountryList()}
          </div>
        </div>
      </div>
      <LeafModal open={showQRCodeModal} onCloseCallbackFn={qrCodeModalToggle} id={"impersonation__container"}>
        <p>Please use the QR code to log into Amway+ on your mobile device.</p>
        <QRCodeSVG value={qrCodeURL} />
        <CopyToClipboard copyText={qrCodeURL} />
      </LeafModal>
    </div>
  );
};
